"use client";

import { Box, useTabsContext } from "@chakra-ui/react";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  type ReactNode,
} from "react";
import { useThemeTokens } from "@/lib/theme/tokens";
import { TabList } from "./Tabs";
import { Text, type TextProps } from "./Text";

/** Hide horizontal scrollbar while keeping overflow scroll on the wrapper. */
export const hideHorizontalScrollbarCss = {
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": {
    display: "none",
    width: 0,
    height: 0,
  },
} as const;

/** Shared tab triggers: single row, labels never wrap; parent scrolls on X when needed. */
export const scrollableTabButtonProps = {
  display: "flex" as const,
  alignItems: "center" as const,
  gap: 2,
  py: 4,
  px: 4,
  fontWeight: "500",
  fontSize: "sm",
  whiteSpace: "nowrap" as const,
  flexShrink: 0,
  flexGrow: 0,
  minW: "max-content",
};

/** Tab label that inherits active/inactive color from StyledTab (use with ScrollableTabList). */
export function ScrollableTabLabel({ children, ...props }: TextProps) {
  return (
    <Text as="span" whiteSpace="nowrap" color="inherit" {...props}>
      {children}
    </Text>
  );
}

export interface ScrollableTabListProps {
  children: ReactNode;
}

const TAB_SCROLL_EDGE_PADDING = 12;

export function ScrollableTabList({ children }: ScrollableTabListProps) {
  const theme = useThemeTokens();
  const { selectedIndex } = useTabsContext();
  const tabListRef = useRef<HTMLDivElement | null>(null);
  const tabStripLeftShadeRef = useRef<HTMLDivElement | null>(null);
  const tabStripRightShadeRef = useRef<HTMLDivElement | null>(null);

  const tabStripFadeSolid =
    theme.mode === "dark"
      ? "rgba(11, 13, 16, 1)"
      : "rgba(246, 248, 251, 1)";

  const syncTabStripShades = useCallback(() => {
    const el = tabListRef.current;
    const left = tabStripLeftShadeRef.current;
    const right = tabStripRightShadeRef.current;
    if (!el || !left || !right) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 2) {
      left.style.opacity = "0";
      right.style.opacity = "0";
      return;
    }
    const { scrollLeft } = el;
    left.style.opacity = scrollLeft > 2 ? "1" : "0";
    right.style.opacity = scrollLeft < maxScroll - 2 ? "1" : "0";
  }, []);

  const scrollActiveTabIntoView = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      const container = tabListRef.current;
      if (!container) return;

      const tabButtons = container.querySelectorAll<HTMLElement>('[role="tab"]');
      const activeTab =
        tabButtons[selectedIndex] ??
        container.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
      if (!activeTab) return;

      const tabLeft = activeTab.offsetLeft;
      const tabRight = tabLeft + activeTab.offsetWidth;
      const viewLeft = container.scrollLeft;
      const viewWidth = container.clientWidth;
      const viewRight = viewLeft + viewWidth;
      const pad = TAB_SCROLL_EDGE_PADDING;

      let targetScroll = viewLeft;
      if (tabLeft < viewLeft + pad) {
        targetScroll = tabLeft - pad;
      } else if (tabRight > viewRight - pad) {
        targetScroll = tabRight - viewWidth + pad;
      } else {
        syncTabStripShades();
        return;
      }

      const maxScroll = Math.max(0, container.scrollWidth - viewWidth);
      const left = Math.max(0, Math.min(targetScroll, maxScroll));
      if (typeof container.scrollTo === "function") {
        container.scrollTo({ left, behavior });
      } else {
        container.scrollLeft = left;
      }
      syncTabStripShades();
    },
    [selectedIndex, syncTabStripShades],
  );

  useLayoutEffect(() => {
    scrollActiveTabIntoView("auto");
  }, [selectedIndex, scrollActiveTabIntoView]);

  useLayoutEffect(() => {
    const el = tabListRef.current;
    if (!el) return;
    const onScrollOrResize = () => {
      syncTabStripShades();
    };
    onScrollOrResize();
    el.addEventListener("scroll", onScrollOrResize, { passive: true });
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            onScrollOrResize();
            scrollActiveTabIntoView("auto");
          })
        : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener("scroll", onScrollOrResize);
      ro?.disconnect();
    };
  }, [syncTabStripShades, scrollActiveTabIntoView]);

  return (
    <Box position="relative" w="100%" minW={0}>
      <Box
        ref={tabListRef}
        overflowX="auto"
        overflowY="hidden"
        w="100%"
        minW={0}
        borderBottomWidth="1px"
        borderBottomColor={theme.borderGlass.default}
        sx={{
          touchAction: "pan-x",
          WebkitOverflowScrolling: "touch",
          ...hideHorizontalScrollbarCss,
        }}
        css={hideHorizontalScrollbarCss}
      >
        <TabList
          display="flex"
          flexDirection="row"
          flexWrap="nowrap"
          alignItems="stretch"
          gap={2}
          w="max-content"
          minW="100%"
          borderBottomWidth={0}
          mb={0}
          overflowX="hidden"
          overflowY="hidden"
          sx={{
            ...hideHorizontalScrollbarCss,
            "& > *": {
              flexShrink: 0,
              flexGrow: 0,
            },
            "& > button[aria-selected=true] svg": {
              color: theme.accent.primary,
            },
            "& > button[aria-selected=true] span": {
              color: theme.accent.primary,
            },
          }}
          css={hideHorizontalScrollbarCss}
        >
          {children}
        </TabList>
      </Box>
      <Box
        ref={tabStripLeftShadeRef}
        aria-hidden
        pointerEvents="none"
        position="absolute"
        left={0}
        top={0}
        bottom={0}
        w="40px"
        zIndex={1}
        transition="opacity 180ms ease-out"
        sx={{
          background: `linear-gradient(to right, ${tabStripFadeSolid}, transparent)`,
          opacity: 0,
        }}
      />
      <Box
        ref={tabStripRightShadeRef}
        aria-hidden
        pointerEvents="none"
        position="absolute"
        right={0}
        top={0}
        bottom={0}
        w="40px"
        zIndex={1}
        transition="opacity 180ms ease-out"
        sx={{
          background: `linear-gradient(to left, ${tabStripFadeSolid}, transparent)`,
          opacity: 0,
        }}
      />
    </Box>
  );
}
